import { useCallback, useEffect, useState } from 'react';
import styles from './Orders.module.css';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import Modal, { ModalTitle, ModalDivider, DetailRow } from '../../components/Modal/Modal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import Pagination from '../../components/Pagination/Pagination';
import FilterSelect from '../../components/FilterSelect/FilterSelect';
import Pill from '../../components/Pill/Pill';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import { api, mapAdminOrder, deliveryFeeLabel } from '../../lib/api';
import { usePoll } from '../../lib/usePoll';

const NEXT_STATUS = {
    Pending: { label: 'Mark as confirmed', status: 'Confirmed' },
    Confirmed: { label: 'Mark as ready', status: 'Ready' },
    Ready: { label: 'Mark as fulfilled', status: 'Completed' },
};

const STATUS_PARAM = {
    Pending: 'pending',
    Confirmed: 'confirmed',
    Ready: 'ready',
    Completed: 'completed',
    Cancelled: 'cancelled',
};

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [fulfillmentFilter, setFulfillmentFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [page, setPage] = useState(1);
    const [viewedOrder, setViewedOrder] = useState(null);
    const [cancelOrder, setCancelOrder] = useState(null);

    const loadOrders = useCallback(async () => {
        const params = new URLSearchParams({ page: String(page), limit: '20' });
        if (search.trim()) params.set('search', search.trim());
        if (STATUS_PARAM[statusFilter]) params.set('status', STATUS_PARAM[statusFilter]);
        try {
            const data = await api(`/admin/orders?${params.toString()}`);
            let mapped = data.orders.map(mapAdminOrder);
            // Fulfilment isn't a server filter — apply it client-side
            if (fulfillmentFilter !== 'All') {
                mapped = mapped.filter((o) => o.fulfillment === fulfillmentFilter);
            }
            setOrders(mapped);
            setTotalPages(Math.max(1, data.pages));
        } catch {
            setOrders([]);
        }
    }, [search, fulfillmentFilter, statusFilter, page]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    // Nothing pushes to the portal. New orders arrive without warning, and this table is where they are
    // watched from.
    usePoll(loadOrders, 30000);

    const filteredOrders = orders;

    const openCancelModal = (order) => {
        setViewedOrder(null);
        setCancelOrder(order);
    };

    const handleAdvanceStatus = async (order) => {
        const next = NEXT_STATUS[order.status];
        if (!next) return;
        try {
            await api(`/admin/orders/${order._id}/advance`, { method: 'POST' });
            await loadOrders();
        } catch (err) {
            alert(err.message);
        }
        setViewedOrder(null);
    };

    const handleCancel = async () => {
        try {
            await api(`/admin/orders/${cancelOrder._id}/cancel`, { method: 'POST' });
            await loadOrders();
        } catch (err) {
            alert(err.message);
        }
        setCancelOrder(null);
    };

    const getMenuItems = (order) => {
        const items = [{ label: 'View order', onClick: () => setViewedOrder(order) }];
        const next = NEXT_STATUS[order.status];
        if (next) {
            items.push({ label: next.label, onClick: () => handleAdvanceStatus(order) });
            items.push({ label: 'Cancel order', danger: true, onClick: () => openCancelModal(order) });
        }
        return items;
    };

    return (
        <div className={`${styles.card} p-4`}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <h2 className={`${styles.cardTitle} mb-0`}>All Orders</h2>

                <div className="d-flex align-items-center flex-wrap gap-3">
                    <FilterSelect
                        label="Fulfilment"
                        value={fulfillmentFilter}
                        options={['All', 'Pickup', 'Delivery']}
                        onChange={setFulfillmentFilter}
                    />
                    <FilterSelect
                        label="Status"
                        value={statusFilter}
                        options={['All', 'Pending', 'Confirmed', 'Ready', 'Completed', 'Cancelled']}
                        onChange={setStatusFilter}
                    />
                    <SearchInput
                        placeholder="Search for orders..."
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
            </div>

            <div className={`${styles.tableWrap} table-responsive mt-3`}>
                <table className={`${styles.table} w-100`}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Buyer</th>
                            <th>Item</th>
                            <th>Fulfillment</th>
                            <th>Total</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.map((order) => (
                            <tr key={order._id}>
                                <td className={styles.orderId}>{order.id}</td>
                                <td>{order.buyer}</td>
                                <td>{order.item}</td>
                                <td>{order.fulfillment}</td>
                                <td className={styles.total}>{order.total}</td>
                                <td><StatusBadge status={order.status} /></td>
                                <td className="text-end position-relative">
                                    <ActionMenu ariaLabel="Order actions" items={getMenuItems(order)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} totalPages={totalPages} onChange={setPage} />

            {viewedOrder && (
                <Modal onClose={() => setViewedOrder(null)}>
                    <ModalTitle>Order {viewedOrder.id}</ModalTitle>

                    <div className="mt-2">
                        <StatusBadge status={viewedOrder.status} />
                    </div>

                    <ModalDivider />

                    {viewedOrder.items.map((item) => (
                        <div key={item.name} className="d-flex align-items-center gap-3 mb-3">
                            <div className={styles.itemThumb}>
                                <img src={item.image} alt={item.name} />
                            </div>
                            <div className="flex-grow-1">
                                <div className={styles.itemName}>{item.name}</div>
                                <div className={styles.itemDetail}>{item.detail}</div>
                            </div>
                            <div className={styles.itemPrice}>{item.price}</div>
                        </div>
                    ))}

                    <ModalDivider />

                    <DetailRow label="Buyer" className="mb-3"><span>{viewedOrder.buyer}</span></DetailRow>
                    <DetailRow label="Seller" className="mb-3"><span>{viewedOrder.seller}</span></DetailRow>
                    <DetailRow label="Fulfilment"><Pill>{viewedOrder.fulfillment}</Pill></DetailRow>

                    <ModalDivider />

                    <DetailRow label="Total to pay" className="mb-3"><span className={styles.total}>{viewedOrder.totalToPay}</span></DetailRow>
                    {/* Quoted by the seller and paid to them on handover, so it
                        is deliberately not part of "Total to pay" — shown here
                        only so a support query about the amount can be answered. */}
                    {viewedOrder.deliveryFee != null && (
                        <DetailRow label="Seller's delivery charge" className="mb-3">
                            <span>{deliveryFeeLabel(viewedOrder.deliveryFee)}</span>
                        </DetailRow>
                    )}
                    <DetailRow label="Payment" className="mb-3"><span>{viewedOrder.payment}</span></DetailRow>
                    <DetailRow label="Order date"><span className={styles.total}>{viewedOrder.orderDate}</span></DetailRow>
                    {viewedOrder.status === 'Completed' && (
                        <>
                            <DetailRow label="Completed by" className="mt-3 mb-3"><span>{viewedOrder.completedBy}</span></DetailRow>
                            <DetailRow label="Commission"><span>{viewedOrder.commission}</span></DetailRow>
                        </>
                    )}

                    {NEXT_STATUS[viewedOrder.status] && (
                        <>
                            <ModalDivider />

                            <div className="d-flex gap-3 mt-4">
                                <Button variant="outline-danger" className="flex-fill" onClick={() => openCancelModal(viewedOrder)}>
                                    Cancel order
                                </Button>
                                <Button className="flex-fill" onClick={() => handleAdvanceStatus(viewedOrder)}>
                                    {NEXT_STATUS[viewedOrder.status].label}
                                </Button>
                            </div>
                        </>
                    )}
                </Modal>
            )}

            {cancelOrder && (
                <ConfirmModal
                    title="Cancel order"
                    confirmLabel="Cancel order"
                    onConfirm={handleCancel}
                    onClose={() => setCancelOrder(null)}
                />
            )}
        </div>
    );
};

export default Orders;
